import {
  getStoredRefreshToken,
  LS_REFRESH_TOKEN,
  setStoredRefreshToken,
} from '../auth/authStorage';

type AuthRefreshResponse = {
  accessToken: string;
  refreshToken: string;
};

type RefreshSessionParams = {
  baseUrl: string;
};

const AUTH_REFRESH_LOCK_NAME = 'dokia-auth-refresh';

let onAccessTokenRefreshed: ((accessToken: string) => void) | undefined;
let onSessionExpired: (() => void) | undefined;
let refreshPromise: Promise<string> | undefined;

export class AuthSessionExpiredError extends Error {
  name = 'AuthSessionExpiredError';

  constructor() {
    super('Session expired');
  }
}

export function clearAuthSessionRefreshToken(): void {
  localStorage.removeItem(LS_REFRESH_TOKEN);
}

export function getAuthSessionRefreshToken(): string | undefined {
  return getStoredRefreshToken();
}

export async function refreshAuthSessionAccessToken(
  parameters: RefreshSessionParams,
): Promise<string> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = withAuthRefreshLock(() =>
    runAuthSessionRefresh(parameters),
  ).finally(() => {
    refreshPromise = undefined;
  });

  return refreshPromise;
}

export async function refreshAuthSessionFromCookie(
  parameters: RefreshSessionParams,
): Promise<string> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = withAuthRefreshLock(() =>
    runAuthSessionCookieRefresh(parameters),
  ).finally(() => {
    refreshPromise = undefined;
  });

  return refreshPromise;
}

export function setAuthSessionAccessTokenRefreshedCallback(
  callback: ((accessToken: string) => void) | undefined,
): void {
  onAccessTokenRefreshed = callback;
}

export function setAuthSessionExpiredCallback(
  callback: (() => void) | undefined,
): void {
  onSessionExpired = callback;
}

export function setAuthSessionRefreshToken(refreshToken: string): void {
  setStoredRefreshToken(refreshToken);
}

function expireAuthSession(): void {
  clearAuthSessionRefreshToken();
  onSessionExpired?.();
}

function isAuthRefreshResponse(data: unknown): data is AuthRefreshResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    'accessToken' in data &&
    typeof data.accessToken === 'string' &&
    'refreshToken' in data &&
    typeof data.refreshToken === 'string'
  );
}

async function runAuthSessionCookieRefresh(
  parameters: RefreshSessionParams,
): Promise<string> {
  const refreshResponse = await globalThis.fetch(
    `${parameters.baseUrl}/auth/token/refresh`,
    {
      credentials: 'include',
      method: 'POST',
    },
  );

  if (!refreshResponse.ok) {
    throw new AuthSessionExpiredError();
  }

  const responseBody: unknown = await refreshResponse.json();
  if (!isAuthRefreshResponse(responseBody)) {
    throw new AuthSessionExpiredError();
  }

  // Cookie-only sessions stay httpOnly: do not copy refresh token to LS.
  onAccessTokenRefreshed?.(responseBody.accessToken);

  return responseBody.accessToken;
}

async function runAuthSessionRefresh(
  parameters: RefreshSessionParams,
  didRetryAfterRotation = false,
): Promise<string> {
  const refreshToken = getAuthSessionRefreshToken();
  if (!refreshToken) {
    // Anonymous / cookie-only boot: do not trigger session-expired logout.
    return runAuthSessionCookieRefresh(parameters);
  }

  const refreshResponse = await globalThis.fetch(
    `${parameters.baseUrl}/auth/token/refresh`,
    {
      body: JSON.stringify({refreshToken}),
      credentials: 'include',
      headers: {'Content-Type': 'application/json'},
      method: 'POST',
    },
  );

  if (!refreshResponse.ok) {
    const currentToken = getAuthSessionRefreshToken();
    if (
      !didRetryAfterRotation
      && currentToken
      && currentToken !== refreshToken
    ) {
      // Another tab rotated successfully — retry once with the new token.
      return runAuthSessionRefresh(parameters, true);
    }
    expireAuthSession();
    throw new AuthSessionExpiredError();
  }

  const responseBody: unknown = await refreshResponse.json();
  if (!isAuthRefreshResponse(responseBody)) {
    expireAuthSession();
    throw new AuthSessionExpiredError();
  }

  setAuthSessionRefreshToken(responseBody.refreshToken);
  onAccessTokenRefreshed?.(responseBody.accessToken);

  return responseBody.accessToken;
}

async function withAuthRefreshLock<T>(
  run: () => Promise<T>,
): Promise<T> {
  // Web Locks API: serialize refresh across tabs (same origin).
  return globalThis.navigator.locks.request(AUTH_REFRESH_LOCK_NAME, run);
}
